import React, { useState, useEffect } from 'react'
import { Bell, ShoppingCart, Check, X, ExternalLink, RefreshCw } from 'lucide-react'
import { alertsAPI } from '../services/api'
import toast from 'react-hot-toast'

const BROKER_URLS = {
  kite_pm: 'https://kite.zerodha.com/', kite_rj: 'https://kite.zerodha.com/',
  angelone_rj: 'https://trade.angelone.in/', angelone_pm: 'https://trade.angelone.in/',
}

const sevColor = (s) => s === 'critical' ? 'border-l-danger-500 bg-danger-50/40'
  : s === 'warning' ? 'border-l-amber-500 bg-amber-50/40' : 'border-l-blue-500 bg-blue-50/40'

/**
 * AlertFeed — in-app feed of fired alerts.
 * @param onReviewOrder(suggestedOrder, event) — opens the prefilled OrderModal
 */
const AlertFeed = ({ onReviewOrder, refreshKey }) => {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [evaluating, setEvaluating] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await alertsAPI.getEvents()
      setEvents(res.data.events || [])
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [refreshKey])

  const runNow = async () => {
    setEvaluating(true)
    try {
      const res = await alertsAPI.evaluate()
      toast.success(res.data.fired ? `${res.data.fired} alert(s) fired` : 'No alerts — all healthy')
      load()
    } catch { toast.error('Evaluation failed') }
    finally { setEvaluating(false) }
  }

  const setStatus = async (event, status) => {
    try {
      await alertsAPI.updateEvent(event._id, { status })
      setEvents(es => es.map(e => e._id === event._id ? { ...e, status } : e))
    } catch { toast.error('Failed to update') }
  }

  // Sort by severity (critical > warning > info), then by status (new first), then newest first
  const SEVERITY_RANK = { critical: 0, warning: 1, info: 2 }
  const STATUS_RANK   = { new: 0, read: 1, acted: 2 }
  const visible = events
    .filter(e => e.status !== 'dismissed')
    .sort((a, b) => {
      const s = (SEVERITY_RANK[a.severity] ?? 99) - (SEVERITY_RANK[b.severity] ?? 99)
      if (s !== 0) return s
      const st = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99)
      if (st !== 0) return st
      return new Date(b.createdAt) - new Date(a.createdAt)
    })

  // Build a display title that puts the SYMBOL up front; strip "SYMBOL: " prefix from raw title
  const splitTitle = (event) => {
    const t = event.title || ''
    const m = t.match(/^([A-Z0-9\-&]+):\s*(.+)$/)
    if (m) return { symbol: m[1], rest: m[2] }
    if (event.symbol) return { symbol: event.symbol, rest: t }
    return { symbol: null, rest: t }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Alerts</h3>
          {visible.filter(e => e.status === 'new').length > 0 && (
            <span className="text-xs bg-danger-100 text-danger-700 font-semibold px-2 py-0.5 rounded-full">
              {visible.filter(e => e.status === 'new').length} new
            </span>
          )}
        </div>
        <button onClick={runNow} disabled={evaluating} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${evaluating ? 'animate-spin' : ''}`} /> Check now
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-400">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-400">No alerts. Your portfolio looks healthy. ✅</div>
      ) : (
        <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1" style={{ scrollbarGutter: 'stable' }}>
          {visible.map(event => {
            const so = event.suggestedOrder
            const { symbol, rest } = splitTitle(event)
            return (
              <div key={event._id} className={`border-l-4 rounded-r-lg p-3 ${sevColor(event.severity)} ${event.status === 'new' ? '' : 'opacity-70'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {symbol && (
                        <span className={`text-base font-bold tracking-tight ${
                          event.severity === 'critical' ? 'text-danger-700' :
                          event.severity === 'warning'  ? 'text-amber-700' : 'text-blue-700'
                        }`}>{symbol}</span>
                      )}
                      <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                        event.severity === 'critical' ? 'bg-danger-100 text-danger-700' :
                        event.severity === 'warning'  ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                      }`}>{event.severity}</span>
                      <span className="text-sm text-gray-800">{rest}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{event.message}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {event.status !== 'acted' && (
                      <button onClick={() => setStatus(event, 'read')} className="p-1.5 text-gray-400 hover:text-success-600 rounded" title="Mark read"><Check className="w-3.5 h-3.5" /></button>
                    )}
                    <button onClick={() => setStatus(event, 'dismissed')} className="p-1.5 text-gray-400 hover:text-danger-600 rounded" title="Dismiss"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>

                {/* Suggested order */}
                {so && (
                  <div className="mt-2 flex items-center justify-between bg-white/70 rounded-lg px-3 py-2 border border-gray-200 gap-3">
                    <span className="text-xs text-gray-600 min-w-0 flex-1">
                      Suggested: <strong>SELL {so.quantity} {so.tradingsymbol}</strong> @ ₹{so.price}
                    </span>
                    {so.executable ? (
                      <button onClick={() => onReviewOrder?.(so, event)} className="shrink-0 flex items-center gap-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 px-3 py-1.5 rounded-lg transition-colors">
                        <ShoppingCart className="w-3.5 h-3.5" /> Review Order
                      </button>
                    ) : (
                      <a href={BROKER_URLS[event.account] || 'https://kite.zerodha.com/'} target="_blank" rel="noreferrer" className="shrink-0 flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 px-3 py-1.5 rounded-lg">
                        Manual <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
                {event.status === 'acted' && <p className="text-xs text-success-600 mt-1.5 font-medium">✓ Order placed</p>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default AlertFeed
